import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { shiftActions } from "../../sagas/shiftSlice";
import { posActions } from "../../sagas/posSlice";
import { commercialActions } from "../../sagas/commercialSlice";
import { Button, Card, CardBody, Col, Container, FormGroup, Input, Label, Row, Spinner } from "reactstrap";
import { FaEdit, FaEye, FaFilter, FaUserClock } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import Select from "react-select";
import { formatPrice } from "../../helpers/utility";
import { SHIFT_DETAIL_PAGE } from "../../routes/routeConstants";
import { useNavigate } from "react-router-dom";

const ShiftPage = () => {
    const { t } = useTranslation();
    const { list: pdvs, loading: pdvLoading } = useSelector(state => state.pos);
    const { list: shifts, loading: shiftLoading } = useSelector(state => state.shift);
    const { list: commercialList, loading: commercialLoading } = useSelector((state) => state.commercials);

    const dispatch = useDispatch();
    const navigate = useNavigate();

    const now = new Date();
    const todayDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const tomorrowDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
    const pad = (n) => n.toString().padStart(2, '0');
    const formatDate = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

    const [filters, setFilters] = useState({
        startDate: formatDate(todayDate),
        endDate: formatDate(tomorrowDate),
        commercialId: null,
        status: null,
        pointOfSaleId: null,
    })


    useEffect(() => {
        dispatch(posActions.list());
        dispatch(commercialActions.list({ query: '' }));
        dispatch(shiftActions.fetchList({
            ...filters
        }))
    }, [])

    useEffect(() => {
        dispatch(shiftActions.fetchList({
            ...filters
        }))
    }, [filters.commercialId, filters.startDate, filters.pointOfSaleId, filters.endDate, filters.status])

    const commercialOptions = commercialList?.map(com => ({
        value: com.id,
        label: com.fullName,
    })) ?? [];
    const commercialOptionsWithAll = [
        { value: null, label: t('common.all', 'Tous') },
        ...commercialOptions
    ];

    const pdvOptions = pdvs?.map(pdv => ({
        value: pdv.id,
        label: pdv.code + ' - ' + pdv.name,
    })) ?? [];

    const pdvOptionsWithAll = [
        { value: null, label: t('common.all', 'Tous') },
        ...pdvOptions
    ];

    const clearFilters = () => {
        setFilters({
            startDate: formatDate(todayDate),
            endDate: formatDate(tomorrowDate),
            commercialId: null,
            status: null,
            pointOfSaleId: null,
        });
        dispatch(shiftActions.fetchList({
            ...filters
        }))
    };

    const handleEdit = (shift) => {
        navigate(SHIFT_DETAIL_PAGE, {
            state: {
                editMode: true,
                shift: shift
            }
        });
    }

    return (
        <Container fluid>
            <Row className="mb-4">
                <Col>
                    <Card className="shadow-sm">
                        <CardBody className="d-flex justify-content-between align-items-center">
                            <h4 className="mb-0 d-flex align-items-center">
                                <FaUserClock className="text-primary me-2" />
                                {t('shifts.title')}
                            </h4>

                        </CardBody>
                    </Card>
                </Col>
            </Row>
            <Card className="shadow-sm">
                <CardBody>
                    {shiftLoading ? ( // Show spinner when loading
                        <div className="text-center py-5">
                            <Spinner color="primary" />
                            <div className="mt-2 text-muted">{t('common.loading')}</div>
                        </div>
                    ) : (
                        <>
                            <div className="filter-container mb-4">
                                <Row className="mb-3 g-2 align-items-end">
                                    <Col md={3}>
                                        <FormGroup className="mb-0">
                                            <Label htmlFor="pointOfSaleId">{t('shifts.pos')}:</Label>
                                            <Select
                                                id="pointOfSaleId"
                                                options={pdvOptionsWithAll}
                                                value={pdvOptionsWithAll.find(option => option.value === filters.pointOfSaleId)}
                                                onChange={(selectedOption) => {
                                                    setFilters(f => ({ ...f, pointOfSaleId: selectedOption ? selectedOption.value : null }));
                                                }}
                                                placeholder={t('shifts.select')}
                                                isSearchable
                                                menuPortalTarget={typeof window !== 'undefined' ? document.body : null}
                                                isClearable
                                                styles={{
                                                    menuPortal: base => ({ ...base, zIndex: 9999 })
                                                }}
                                            />
                                        </FormGroup>
                                    </Col>
                                    <Col md={2}>
                                        <FormGroup className="mb-0">
                                            <Label htmlFor="commercialId">{t('shifts.caissier')}:</Label>
                                            <Select
                                                id="commercialId"
                                                options={commercialOptionsWithAll}
                                                value={commercialOptionsWithAll.find(option => option.value === filters.commercialId)}
                                                onChange={(selectedOption) => {
                                                    setFilters(f => ({ ...f, commercialId: selectedOption ? selectedOption.value : null }));
                                                }}
                                                placeholder={t('shifts.select')}
                                                isSearchable
                                                isClearable
                                                menuPortalTarget={typeof window !== 'undefined' ? document.body : null}
                                                styles={{
                                                    menuPortal: base => ({ ...base, zIndex: 9999 })
                                                }}
                                            />
                                        </FormGroup>
                                    </Col>
                                    <Col lg={2} md={3} sm={6} xs={12}>
                                        <FormGroup className="mb-0">
                                            <Label for="startDate" className="mb-1">Date début</Label>
                                            <Input
                                                type="date"
                                                id="startDate"
                                                value={filters.startDate}
                                                onChange={e => setFilters(f => ({ ...f, startDate: e.target.value }))}
                                            />
                                        </FormGroup>
                                    </Col>
                                    <Col lg={2} md={3} sm={6} xs={12}>
                                        <FormGroup className="mb-0">
                                            <Label for="dateTo" className="mb-1">Date fin</Label>
                                            <Input
                                                type="date"
                                                id="endDate"
                                                value={filters.endDate}
                                                onChange={e => setFilters(f => ({ ...f, endDate: e.target.value }))}
                                            />
                                        </FormGroup>
                                    </Col>
                                    <Col lg={2} md={3} sm={6} xs={12}>
                                        <FormGroup className="mb-0">
                                            <Label className="mb-1">{t('shifts.status')}</Label>
                                            <Input
                                                type="select"
                                                value={filters.status}
                                                onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                                            >
                                                <option value="">{t('shifts.status')}</option>
                                                <option value="OPENED">{t('shifts.opened')}</option>
                                                <option value="CLOSED">{t('shifts.closed')}</option>
                                                <option value="VALIDATED">{t('shifts.validated')}</option>
                                            </Input>
                                        </FormGroup>
                                    </Col>
                                    <Col lg={1} md={1} sm={6} xs={12} className="d-flex align-items-end">
                                        <Button
                                            color="secondary"
                                            onClick={clearFilters}
                                            title={t('common.clearFilters')}
                                            className="mb-3"
                                            style={{ minHeight: '38px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                        >
                                            <FaFilter />
                                        </Button>
                                    </Col>
                                </Row>
                            </div>
                            <table className="table table-hover table-striped table-bordered">
                                <thead className="thead-light">
                                    <tr>
                                        <th className="align-middle text-center">{t('shifts.code')}</th>
                                        <th className="align-middle text-center">{t('shifts.caissier')}</th>
                                        <th className="align-middle text-center">{t('shifts.openingTime')}</th>
                                        <th className="align-middle text-center">{t('shifts.closingTime')}</th>
                                        <th className="align-middle text-center">{t('shifts.closingAmountCash')}</th>
                                        <th className="align-middle text-center">{t('shifts.closingAmountCard')}</th>
                                        <th className="align-middle text-center">{t('shifts.status')}</th>
                                        <th className="align-middle text-center">{t('common.actions')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {shifts.map((shift) => (
                                        <tr key={shift.id}>
                                            <td className="align-middle text-center">{shift.shiftCode}</td>
                                            <td className="align-middle text-center">{shift.commercialName}</td>
                                            <td className="align-middle text-center">{shift.openingTime}</td>
                                            <td className="align-middle text-center">{shift.closingTime}</td>
                                            <td className="align-middle text-center">{formatPrice(shift.closingAmountCash)}</td>
                                            <td className="align-middle text-center">{formatPrice(shift.closingAmountCard)}</td>
                                            <td className="align-middle text-center">
                                                {shift.status === 'OPENED' && (
                                                    <span className="badge bg-warning">{t('shifts.opened')}</span>
                                                )}
                                                {shift.status === 'CLOSED' && (
                                                    <span className="badge bg-danger">{t('shifts.closed')}</span>
                                                )}
                                                {shift.status === 'VALIDATED' && (
                                                    <span className="badge bg-success">{t('shifts.validated')}</span>
                                                )}
                                            </td>
                                            <td>
                                                <Button
                                                    color="warning"
                                                    title={t('shifts.consult')}
                                                    onClick={() => handleEdit(shift)}
                                                    size="sm"
                                                    className="me-1"
                                                >
                                                    <FaEdit />
                                                </Button>
                                            </td>
                                        </tr>
                                    ))}
                                    {shifts.length === 0 && (
                                        <tr>
                                            <td colSpan="8" className="text-center py-4">
                                                <div className="text-muted">{t('shifts.noShifts')}</div>
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </>
                    )}
                </CardBody>
            </Card>
        </Container>
    )
}

export default ShiftPage;